<script setup lang="ts">
/**
 * DesignPreview — a dev-only visual harness at `#/preview`.
 *
 * Renders the real presentational components (AssistantCard, MessageBubble,
 * ChatInput) with hardcoded mock data so the populated design can be
 * reviewed without a backend or auth. Not linked from any nav; harmless in
 * prod. Delete once the redesign has shipped and been verified live.
 */
import { reactive } from "vue";
import AssistantCard from "../components/AssistantCard.vue";
import ChatInput from "../components/ChatInput.vue";
import MessageBubble from "../components/MessageBubble.vue";
import HelpHint from "../components/HelpHint.vue";
import type { ChatTurn } from "../types/chat";

const cards = [
  {
    name: "Contract Intelligence",
    icon: "file-search",
    description:
      "Ask about renewals, billability and obligations across every Risma contract.",
    samples: [
      "Which contracts renew before end of Q3?",
      "Is the Northwind deal billable?",
    ],
  },
  {
    name: "HR Assistant",
    icon: "users",
    description:
      "Leave policy, parental rules, expenses — answered from the current handbook.",
    samples: [
      "How much parental leave do I get?",
      "What's the expense limit for travel?",
    ],
  },
  {
    name: "Booking Matcher",
    icon: "user-search",
    description:
      "Find the right consultant for a brief by skills, availability and past work.",
    samples: ["Who's free for a Power BI job in August?"],
  },
  {
    name: "AI Governance & Compliance",
    icon: "shield-check",
    // Deliberately long — exercises the tagline word-boundary truncation.
    description:
      "Guides Pharma & Life Science teams through AI-system validation, risk classification, and the FDA / EU AI Act / GAMP - AI / PIC/S Annex 22 requirements.",
    samples: [],
  },
  {
    // No icon set → monogram fallback ("PA").
    name: "Procurement Advisor",
    icon: undefined as string | undefined,
    description:
      "Supplier policy, thresholds and approval routes, with sources.",
    samples: [],
  },
];

const turns = reactive<ChatTurn[]>([
  {
    id: "u1",
    role: "user",
    content:
      "Which contracts renew before the end of Q3, and which are billable?",
  },
  {
    id: "a1",
    role: "assistant",
    messageId: 1,
    content:
      "## Renewals before end of Q3\n\nThree contracts renew before 30 September [1]:\n\n- **Northwind FM** — renews 12 Aug, billable\n- **Risma Compliance** — renews 3 Sep, billable [2]\n- **Statsbiblioteket** — renews 28 Sep, *not* billable\n\n| Contract | Renews | Billable |\n| --- | --- | --- |\n| Northwind FM | 2026-08-12 | Yes |\n| Risma Compliance | 2026-09-03 | Yes |\n| Statsbiblioteket | 2026-09-28 | No |\n\nThe non-billable item is covered under the master framework agreement [1].",
    retrieval: {
      type: "retrieval",
      filter: "",
      groups: ["group:contracts-confidential"],
      hits: [
        {
          n: 1,
          id: "c1",
          title: "Framework Agreement 2026",
          url: "",
          source: "sharepoint",
          acl: [],
          score: 0.82,
          snippet:
            "Contracts under the master framework are not separately billable; they are covered by the annual platform fee.",
        },
        {
          n: 2,
          id: "c2",
          title: "Risma Compliance SOW",
          url: "https://example.com",
          source: "sharepoint",
          acl: [],
          score: 0.78,
          snippet:
            "Annual renewal on 3 September, billed quarterly in advance.",
        },
      ],
    },
    tools: [
      {
        id: "t1",
        name: "run_sql",
        arguments: {
          query:
            "select name, renews_on, billable from fact_contracts where renews_on < '2026-10-01'",
        },
        result: {
          type: "tool_result",
          id: "t1",
          name: "run_sql",
          ok: true,
          payload: { rows_preview: [{ contract: "Northwind FM" }] },
        },
      },
    ],
  },
]);

const samples = [
  "Which contracts renew before end of Q3?",
  "Summarise our parental-leave policy",
  "Who's available for a Power BI engagement in August?",
];
</script>

<template>
  <div class="preview">
    <p class="preview__tag eyebrow">Design preview · mock data</p>

    <h2 class="preview__h display">Assistant cards</h2>
    <div class="preview__grid">
      <AssistantCard
        v-for="(c, i) in cards"
        :key="c.name"
        :slug="`mock-${i}`"
        :name="c.name"
        :description="c.description"
        :icon="c.icon"
        :href="`#/chat/mock-${i}`"
        :index="i"
      />
    </div>

    <h2 class="preview__h display">Chat surface</h2>
    <div class="preview__chat">
      <section class="transcript glass">
        <MessageBubble v-for="t in turns" :key="t.id" :turn="t" />
      </section>
      <ChatInput :samples="samples" placeholder="Ask Contract Intelligence…" />
    </div>

    <h2 class="preview__h display">
      Help hint
      <HelpHint
        text="This is what an inline help popover looks like — short, plain-language, in the interface's own voice."
      />
    </h2>
  </div>
</template>

<style scoped>
.preview {
  /* Definite width (not max-width) — see AssistantHub `.hub`: an auto-margin
     grid item collapses to content width under `max-width` alone. */
  width: min(1280px, 100%);
  margin-inline: auto;
  padding: var(--space-5) var(--space-4) var(--space-8);
}
.preview__tag {
  color: var(--color-accent);
  margin-bottom: var(--space-4);
}
.preview__h {
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--color-ink);
  margin: var(--space-6) 0 var(--space-4);
}
/* Mirror the real Hub grid so this harness reflects production proportions. */
.preview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  gap: var(--space-4);
}
@media (max-width: 560px) {
  .preview__grid {
    grid-template-columns: 1fr;
  }
}
.preview__chat {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.transcript {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
}
</style>
